import { LegalPage } from '../../components/LegalPage';
import { CONTACT_EMAIL, OPERATOR, OPERATOR_DESCRIPTION } from '../../lib/legal';

const mail = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

export function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      summary={
        <ul className="list-disc space-y-1 pl-5">
          <li>We collect only what we need to run your account and keep MOGZO safe.</li>
          <li>
            We don’t sell your data or show ads. We count visits with Cloudflare Web Analytics,
            which uses no cookies and doesn’t follow you to other sites.
          </li>
          <li>For your age we keep only the month and year you were born.</li>
          <li>You can ask us to see, correct or delete your data at any time: {mail}.</li>
        </ul>
      }
    >
      <h2>1. Who we are</h2>
      <p>
        MOGZO (mogzo.com) is run by {OPERATOR_DESCRIPTION} (“{OPERATOR}”, “we”, “us”). We decide how
        and why your personal data is used, which makes us the data fiduciary (or data controller)
        for it. You can reach us at {mail}.
      </p>
      <p>
        MOGZO is in early access. As we add features such as games, posts and communities, we will
        update this policy before those features collect anything new.
      </p>

      <h2>2. What we collect</h2>
      <h3>When you create an account</h3>
      <ul>
        <li>
          <strong>Email address and display name.</strong>
        </li>
        <li>
          <strong>Password.</strong> We store only a one-way scrambled form (an Argon2id hash). We
          can’t see or recover your password.
        </li>
        <li>
          <strong>Month and year of birth.</strong> Never the full date. We use it only to check
          that you’re old enough for an account and to apply protections for younger players.
        </li>
      </ul>
      <h3>When you sign in with Google</h3>
      <p>
        Google shares your name, email address, profile picture link and Google account ID with us.
        We don’t get your Google password or access to anything else in your Google account.
      </p>
      <h3>When you apply to become a developer</h3>
      <p>
        Your name, email address, studio or team name (if you give one), country, the engine you
        use, links to your work, what you want to make, your messages with our review team, and the
        IP address the application was sent from.
      </p>
      <h3>When you use MOGZO</h3>
      <ul>
        <li>
          <strong>Security records:</strong> the IP address and browser type used for each session,
          and a log of account events such as sign-ins, password resets and changes to two-factor
          authentication.
        </li>
        <li>
          <strong>Two-factor authentication:</strong> if you turn it on, an encrypted secret for
          your authenticator app and your backup codes.
        </li>
        <li>
          <strong>Game saves and game data:</strong> progress and content the games you play store
          with us, linked to your account and kept apart for each game. Some games share what you
          make in them, such as a level, with their other players. Games never see your email or
          account.
        </li>
        <li>
          <strong>Leaderboard scores:</strong> your best scores in a game, shown with your display
          name to that game’s other players.
        </li>
        <li>
          <strong>Achievements:</strong> which achievements you’ve unlocked in a game, when, and
          your progress towards them. The game and its developer see these; developers see testers’
          names, and live players only as anonymous ids.
        </li>
        <li>
          <strong>Messages you send us</strong>, such as support or privacy requests.
        </li>
        <li>
          <strong>Usage statistics:</strong> the pages you visit, the site that sent you, your
          browser and type of device, your country and how fast pages load. Cloudflare Web Analytics
          counts these for us without cookies and without identifying you.
        </li>
      </ul>
      <p>
        We don’t collect your phone number, address, contacts, precise location or payment details.
      </p>

      <h2>3. Why we use it</h2>
      <ul>
        <li>To create and run your account, and to sign you in.</li>
        <li>To send emails you need: confirming your address and resetting your password.</li>
        <li>To review developer applications and tell applicants our decision.</li>
        <li>
          To keep MOGZO secure: stopping bots, fake accounts, abuse and unauthorised access to
          accounts.
        </li>
        <li>To understand how MOGZO is used and make it faster and better.</li>
        <li>To meet legal obligations and respond to lawful requests.</li>
      </ul>
      <p>
        We use your data only for these purposes, with your consent (which you give when you create
        an account) or where the law allows it without consent, for example to comply with the law.
        We don’t use it for advertising, and we don’t build profiles to target you.
      </p>

      <h2>4. Cookies</h2>
      <p>
        We use only cookies that are needed for MOGZO to work: cookies that keep you signed in, that
        complete a two-factor or Google sign-in, and (if you choose) that remember a trusted device
        for 30 days. Our “I’m human” check is run by Cloudflare Turnstile, which may set its own
        cookie for that purpose only. We don’t use advertising or analytics cookies (our visit
        counts use no cookies at all), so there is nothing to opt out of.
      </p>

      <h2>5. Who we share it with</h2>
      <p>
        We don’t sell or rent your personal data. We share it only with service providers that help
        us run MOGZO, under agreements that limit how they can use it:
      </p>
      <ul>
        <li>
          <strong>Oracle Cloud:</strong> our servers and database, located in India (Mumbai).
        </li>
        <li>
          <strong>Cloudflare:</strong> website delivery, security, the “I’m human” check, visit
          statistics and encrypted backup storage.
        </li>
        <li>
          <strong>Brevo:</strong> sends our emails.
        </li>
        <li>
          <strong>Google:</strong> only if you choose “Continue with Google”.
        </li>
      </ul>
      <p>
        Some of these providers process data outside India. We may also disclose data if the law
        requires it, to protect people’s safety, or as part of a transfer of the business (we would
        tell you first).
      </p>

      <h2>6. How long we keep it</h2>
      <ul>
        <li>Account data: while your account exists.</li>
        <li>
          Game saves, game data, leaderboard scores and achievements: until you delete your account,
          or the game deletes them.
        </li>
        <li>
          After you ask us to delete your account: deleted within 30 days, and removed from our
          backups within a further 35 days as they expire.
        </li>
        <li>Sessions expire after 30 days of not being used.</li>
        <li>
          Developer applications: while you are a developer; otherwise up to 24 months after our
          decision. Applications whose email was never confirmed: up to 30 days.
        </li>
        <li>Security records: up to 12 months.</li>
      </ul>

      <h2>7. Children and teens</h2>
      <p>
        You must be at least 13 to create a MOGZO account. If someone under 13 tries to sign up, we
        don’t create the account, and if we learn that an account belongs to someone under 13, we
        delete it. Anyone can still play games without an account.
      </p>
      <p>
        If you are under 18 (or under the age of adulthood where you live), you may use MOGZO only
        with the involvement and permission of a parent or guardian. We don’t show ads to anyone,
        and we don’t track or profile younger players. A parent or guardian can contact us at {mail}{' '}
        to review or delete their child’s account.
      </p>

      <h2>8. Your rights</h2>
      <p>You can ask us to:</p>
      <ul>
        <li>tell you what personal data we hold about you and how we use it;</li>
        <li>correct or update it;</li>
        <li>delete it, and close your account;</li>
        <li>withdraw your consent (this means closing your account);</li>
        <li>
          let someone you nominate act for you if you die or can’t act for yourself (under Indian
          law).
        </li>
      </ul>
      <p>
        Email {mail} from the address on your account. We may ask you to confirm it’s you. We don’t
        charge for this. Depending on where you live, you may have further rights under your local
        law, and we will respect them.
      </p>

      <h2>9. How we protect it</h2>
      <p>
        Connections to MOGZO are encrypted, passwords are hashed with Argon2id, two-factor
        authentication is available to everyone (and required for developers and staff), and backups
        are encrypted and stored privately. No system is perfectly secure. If a breach affects your
        data, we will tell you and the authorities as the law requires.
      </p>

      <h2>10. Grievance Officer and complaints</h2>
      <p>
        For any concern about your data or this policy, write to the Grievance Officer, {OPERATOR},
        at {mail}. We acknowledge complaints within 24 hours and aim to resolve them within 15 days.
        If you’re in India and not satisfied with our answer, you may complain to the Data
        Protection Board of India.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We will update this page when our practices change, and change the date at the top. If a
        change is significant, we will also tell you by email or on MOGZO before it takes effect.
      </p>
    </LegalPage>
  );
}
